import { Flex, Text, useToken } from '@chakra-ui/react'
import React from 'react'
import {
  Bar,
  CartesianGrid,
  Legend,
  BarChart as ReBarChart,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  type TooltipContentProps,
  XAxis,
  YAxis,
} from 'recharts'

import { textStyleKeys, textStyles } from '@mangovoice/design-system/textStyles'
import type { CallsReport } from '@mangovoice/types'

import { useIsDarkMode } from '../hooks/useIsDarkMode'
import { useReportingI18n } from '../i18n'
import ChartWrapper from './ChartWrapper'

interface RechartsBarChartProps {
  chartData: CallsReport
  timeZone?: string
  title?: string
  color?: string[]
  height?: number
  showAsPercent?: boolean
  hasModalButton?: boolean
  barWidth?: number
  barRadius?: number | [number, number, number, number]
  showGrid?: boolean
  minBarSize?: number
  showLegend?: boolean
  containerPaddingTop?: string
  headerContent?: React.ReactNode
  hideWrapper?: boolean
  seriesLabels?: Record<string, string>
}

const RechartsBarChart = ({
  chartData,
  timeZone,
  title = 'Bar Chart',
  color = ['blue.500'],
  height = 300,
  showAsPercent = false,
  barWidth = 22,
  barRadius,
  showGrid = true,
  minBarSize,
  showLegend = true,
  containerPaddingTop,
  headerContent,
  hideWrapper = false,
  seriesLabels,
}: RechartsBarChartProps) => {
  const { text, locale } = useReportingI18n()
  const barColors = useToken('colors', color)

  const isDark = useIsDarkMode()

  const lightAxisColors = useToken('colors', [
    'blue.500',
    'gray.500',
    'gray.400',
    'gray.700',
    'gray.500',
    'gray.300',
  ])

  const darkAxisColors = useToken('colors', [
    'blue.400',
    'gray.400',
    'gray.600',
    'gray.300',
    'gray.600',
    'gray.700',
  ])

  const [defaultColor, xTickFill, xStroke, yTickFill, yStroke, gridColor] =
    isDark ? darkAxisColors : lightAxisColors

  const xAxisTextStyle = textStyles[textStyleKeys.Body3]
  const fontSizeString = Array.isArray(xAxisTextStyle.fontSize)
    ? xAxisTextStyle.fontSize[0]
    : xAxisTextStyle.fontSize
  const fontSize = parseFloat(fontSizeString)
  const fontFamily = xAxisTextStyle.fontFamily

  if (!chartData || !chartData.data || chartData.data.length === 0) {
    return (
      <Flex
        direction="column"
        justify="center"
        align="center"
        bg="bg.panel"
        borderRadius="md"
        height={`${height}px`}
      >
        <Text>
          {text('rechartsBarChart.noChartData', 'No chart data available')}
        </Text>
      </Flex>
    )
  }

  const { data, meta } = chartData
  const effectiveTimeZone = timeZone ?? meta.filters.timezone ?? 'UTC'
  const seriesKeys = Object.keys(data[0]).filter((key) => key !== 'date')

  const getTickFormatter = () => {
    const { groupBy } = meta
    if (groupBy.includes('hour')) {
      return (tick: string) =>
        new Date(tick).toLocaleTimeString(locale, {
          hour: '2-digit',
          minute: '2-digit',
          timeZone: effectiveTimeZone,
        })
    }

    return (tick: string) => {
      const date = new Date(tick)
      if (isNaN(date.getTime())) return tick

      const formattedDate = date.toLocaleDateString(locale, {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit',
        timeZone: effectiveTimeZone,
      })

      return groupBy.includes('week')
        ? text('rechartsBarChart.weekOf', 'Week of {{date}}', {
            date: formattedDate,
          })
        : formattedDate
    }
  }

  const CustomTooltip = ({ active, payload }: TooltipContentProps) => {
    if (active && payload && payload.length) {
      type BarTooltipPayload = {
        name?: string
        value?: number
        color?: string
        payload?: { date: string }
      }
      const typedPayload = payload as unknown as readonly BarTooltipPayload[]
      const originalDateStr = typedPayload[0].payload?.date ?? ''
      const originalDate = new Date(originalDateStr)

      const header = meta.groupBy.includes('hour')
        ? originalDate.toLocaleTimeString(locale, {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: effectiveTimeZone,
          })
        : meta.groupBy.includes('week')
          ? text('rechartsBarChart.weekOf', 'Week of {{date}}', {
              date: originalDate.toLocaleDateString(locale, {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
                timeZone: effectiveTimeZone,
              }),
            })
          : originalDate.toLocaleDateString(locale, {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
              timeZone: effectiveTimeZone,
            }) +
            ' - ' +
            originalDate.toLocaleDateString(locale, {
              weekday: 'long',
              timeZone: effectiveTimeZone,
            })

      const values = typedPayload.map((item) => item.value ?? 0)
      const total = values.reduce((sum, val) => sum + val, 0)

      const isAnswerRate =
        typedPayload.some((p) => p.name === 'Answered') &&
        typedPayload.some((p) => p.name === 'Missed')

      const isCallVolume =
        typedPayload.some((p) => p.name === 'Inbound') &&
        typedPayload.some((p) => p.name === 'Outbound')

      return (
        <Flex
          direction="column"
          bg="bg.panel"
          border="1px solid"
          borderColor="border.default"
          borderRadius="4px"
          p="8px"
        >
          <Flex
            {...textStyles[textStyleKeys.Body1_Bold]}
            color="fg.default"
            mb="4px"
          >
            {header}
          </Flex>
          {typedPayload.map((item) => (
            <Flex
              key={item.name}
              {...textStyles[textStyleKeys.Body1]}
              justifyContent="space-between"
              width="100%"
            >
              <Text color="fg.default">
                {(item.name && seriesLabels?.[item.name]) ?? item.name}:
              </Text>
              <Text ml="8px" color={item.color}>
                {item.value}
              </Text>
            </Flex>
          ))}

          {isAnswerRate && (
            <Flex
              {...textStyles[textStyleKeys.Body1_Bold]}
              color="fg.default"
              justifyContent="space-between"
              borderTop="1px solid"
              borderColor="border.default"
              mt="4px"
              pt="4px"
              width="100%"
            >
              <Text>
                {text('rechartsBarChart.tooltip.answerRate', 'Answer Rate:')}
              </Text>
              <Text ml="8px" color="fg.success">
                {total > 0
                  ? `${Math.round(((typedPayload.find((p) => p.name === 'Answered')?.value ?? 0) / total) * 100)}%`
                  : '0%'}
              </Text>
            </Flex>
          )}

          {isCallVolume && (
            <Flex
              {...textStyles[textStyleKeys.Body1_Bold]}
              color="fg.default"
              justifyContent="space-between"
              borderTop="1px solid"
              borderColor="border.default"
              mt="4px"
              pt="4px"
              width="100%"
            >
              <Text>{text('rechartsBarChart.tooltip.total', 'Total:')}</Text>
              <Text ml="8px">{total}</Text>
            </Flex>
          )}
        </Flex>
      )
    }
    return null
  }
  const formatter = getTickFormatter()

  const chartMargin = {
    top: 10,
    right: 20,
    left: 0,
    bottom: seriesKeys.length > 1 && showLegend ? 0 : 20,
  }
  const tickMargin = 10

  const maxLabelsToShow = 10
  const recommendedInterval =
    data.length > maxLabelsToShow
      ? Math.ceil(data.length / maxLabelsToShow) - 1
      : 0

  const adjustedHeight = height

  const BarChart = ({ isInModal = false }: { isInModal?: boolean }) => {
    const chartHeight = isInModal ? '100%' : adjustedHeight - 80
    return (
      <ResponsiveContainer width="100%" height={chartHeight}>
        <ReBarChart
          data={data}
          margin={chartMargin}
          barCategoryGap="10%"
          barGap={0}
        >
          {showGrid && (
            <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
          )}
          <XAxis
            dataKey="date"
            tickFormatter={formatter}
            tickMargin={tickMargin}
            interval={recommendedInterval}
            tick={{
              fill: xTickFill,
              fontFamily: fontFamily,
              style: { fontSize: `${fontSize}px` },
            }}
            stroke={xStroke}
          />
          <YAxis
            domain={showAsPercent ? [0, 100] : [0, 'auto']}
            tickFormatter={
              showAsPercent ? (value: number) => `${value}%` : undefined
            }
            dx={-10}
            tick={{
              fill: yTickFill,
              fontFamily: fontFamily,
              style: { fontSize: `${fontSize}px` },
            }}
            stroke={yStroke}
          />
          <ReTooltip content={CustomTooltip} cursor={{ fill: '#aaaaaa33' }} />
          {showLegend && seriesKeys.length > 1 && (
            <Legend
              verticalAlign="bottom"
              align="center"
              wrapperStyle={{ paddingTop: '20px' }}
              formatter={(value: string) => seriesLabels?.[value] ?? value}
            />
          )}
          {seriesKeys.map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              fill={barColors[index] ?? barColors[0] ?? defaultColor}
              barSize={barWidth}
              {...(barRadius !== undefined && { radius: barRadius })}
              {...(minBarSize !== undefined && { minPointSize: minBarSize })}
            />
          ))}
        </ReBarChart>
      </ResponsiveContainer>
    )
  }

  const chartContent = (
    <>
      {headerContent && (
        <Flex justifyContent="center" mb="1rem">
          {headerContent}
        </Flex>
      )}
      <Flex
        alignItems="center"
        justifyContent="space-between"
        mb="0.5rem"
        mt={headerContent || hideWrapper ? '0' : '-10px'}
      >
        <Text {...textStyles[textStyleKeys.Body2_Bold]}>{title}</Text>
      </Flex>
      <BarChart />
    </>
  )

  if (hideWrapper) {
    return chartContent
  }

  return (
    <ChartWrapper
      height={`${adjustedHeight}px`}
      paddingTop={containerPaddingTop}
    >
      {chartContent}
    </ChartWrapper>
  )
}

export default RechartsBarChart
